import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useGmailApi } from '../hooks/useGmailApi';
import { gmailApi } from '../services/api';
import {
  Plus,
  Mail,
  Clock,
  Play,
  Trash2,
  Edit,
  Zap,
  AlertTriangle,
  Loader2,
  ArrowLeft,
} from 'lucide-react';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[4]};
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    align-items: flex-start;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}15;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[5]};
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: #fff;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    justify-content: center;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
  padding: ${({ theme }) => theme.spacing[6]};
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[4]};
    gap: ${({ theme }) => theme.spacing[4]};
  }
`;

const AutomationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

interface AutomationCardProps {
  active: boolean;
}

const AutomationCard = styled(motion.div) <AutomationCardProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[5]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ active, theme }) =>
    active ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  transition: all 0.2s;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const CardTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  flex: 1;
`;

const StatusBadge = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  background: ${({ active, theme }) =>
    active ? theme.colors.success + '20' : theme.colors.warning + '20'};
  color: ${({ active, theme }) =>
    active ? theme.colors.success : theme.colors.warning};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
`;

const TriggerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textPrimary};

  svg {
    color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textSecondary};

  span {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
  }
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding-top: ${({ theme }) => theme.spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ToggleSwitch = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  cursor: pointer;
  user-select: none;
  flex: 1;

  input {
    display: none;
  }

  span.switch {
    position: relative;
    width: 48px;
    height: 24px;
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.full};
    transition: all 0.3s;

    &::after {
      content: '';
      position: absolute;
      left: 2px;
      top: 2px;
      width: 20px;
      height: 20px;
      background: #fff;
      border-radius: 50%;
      transition: all 0.3s;
    }
  }

  input:checked + span.switch {
    background: ${({ theme }) => theme.colors.primary};

    &::after {
      left: 26px;
    }
  }

  span.label {
    font-size: 0.9rem;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.background};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &.delete {
    &:hover {
      background: ${({ theme }) => theme.colors.error}10;
      border-color: ${({ theme }) => theme.colors.error};
      color: ${({ theme }) => theme.colors.error};
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[10]} ${({ theme }) => theme.spacing[6]};
  text-align: center;

  svg {
    width: 64px;
    height: 64px;
    color: ${({ theme }) => theme.colors.textSecondary};
    opacity: 0.5;
  }

  svg.spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary};
    margin: 0;
  }

  p {
    font-size: 0.95rem;
    color: ${({ theme }) => theme.colors.textSecondary};
    margin: 0;
    max-width: 400px;
  }

  button {
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
    font-size: 0.85rem;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

// Modal de confirmação
const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: ${({ theme }) => theme.spacing[4]};
`;

const ModalContent = styled(motion.div)`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  max-width: 420px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};

  svg {
    color: ${({ theme }) => theme.colors.error};
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const ModalText = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: flex-end;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column-reverse;
    
    button {
      width: 100%;
    }
  }
`;

const ModalButton = styled.button<{ variant?: 'danger' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[5]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${({ theme, variant }) =>
    variant === 'danger' ? theme.colors.error : theme.colors.border};
  background: ${({ theme, variant }) =>
    variant === 'danger' ? theme.colors.error : 'transparent'};
  color: ${({ theme, variant }) =>
    variant === 'danger' ? '#fff' : theme.colors.textPrimary};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    background: ${({ theme, variant }) =>
    variant === 'danger' ? '#d32f2f' : theme.colors.backgroundTertiary};
  }

  &:active {
    transform: translateY(0);
  }
`;



interface Automation {
  id: string;
  name: string;
  trigger: {
    type: 'manual' | 'emailReceived' | 'scheduled';
    labelFilter?: string;
    scheduleTime?: string;
  };
  actions: {
    type: string;
    to: string;
    subject: string;
    body: string;
  }[];
  active: boolean;
  createdAt: Date;
}

const AutomationsList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const userId = user?.uid;
  const { hasToken, isLoadingTokenConfig } = useGmailApi(userId);

  const [automations, setAutomations] = useState<Automation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; id: string; name: string }>({
    show: false,
    id: '',
    name: ''
  });

  const hasShownToast = useRef(false);

  // Redirecionar se não tiver Gmail conectado
  useEffect(() => {
    // Reset do flag quando token for detectado
    if (hasToken) {
      hasShownToast.current = false;
      return;
    }

    // Só redirecionar se userId estiver presente e loading terminou
    if (userId && !isLoadingTokenConfig && !hasToken && !hasShownToast.current) {
      hasShownToast.current = true;
      toast.error('Você precisa conectar sua conta Gmail primeiro');
      navigate('/gmail');
    }
  }, [hasToken, isLoadingTokenConfig, navigate, userId]);

  // Carregar automações da API
  const loadAutomations = React.useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      const response = await gmailApi.listAutomations(userId);

      if (response.status === 'success' && response.data) {
        const loadedAutomations = response.data.automations.map((auto: any) => ({
          ...auto,
          createdAt: new Date(auto.createdAt),
        })) as Automation[];
        setAutomations(loadedAutomations);
      } else {
        toast.error(response.message || 'Erro ao carregar automações');
      }
    } catch (error) {
      console.error('[AutomationsList] Erro ao carregar automações:', error);
      toast.error('Erro ao carregar automações');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadAutomations();
  }, [loadAutomations]);

  const handleToggleActive = async (id: string) => {
    if (!userId) return;

    const automation = automations.find(a => a.id === id);
    if (!automation) return;

    const newActiveState = !automation.active;

    try {
      const response = await gmailApi.toggleAutomation(id, newActiveState, userId);

      if (response.status === 'success') {
        setAutomations(automations.map(auto =>
          auto.id === id ? { ...auto, active: newActiveState } : auto
        ));
        toast.success(newActiveState ? 'Automação ativada!' : 'Automação desativada');
      } else {
        toast.error(response.message || 'Erro ao atualizar automação');
      }
    } catch (error) {
      console.error('[AutomationsList] Erro ao toggle automação:', error);
      toast.error('Erro ao atualizar automação');
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/gmail/automations/${id}/edit`);
  };

  const handleDelete = (id: string) => {
    const automation = automations.find(a => a.id === id);
    if (!automation) return;

    setDeleteModal({
      show: true,
      id,
      name: automation.name
    });
  };

  const confirmDelete = async () => {
    if (!userId) return;

    const { id, name } = deleteModal;

    try {
      const response = await gmailApi.deleteAutomation(id, userId);

      if (response.status === 'success') {
        setAutomations(automations.filter(auto => auto.id !== id));
        setDeleteModal({ show: false, id: '', name: '' });
        toast.success(`Automação "${name}" removida com sucesso`);
      } else {
        toast.error(response.message || 'Erro ao deletar automação');
        setDeleteModal({ show: false, id: '', name: '' });
      }
    } catch (error) {
      console.error('[AutomationsList] Erro ao deletar automação:', error);
      toast.error('Erro ao deletar automação');
      setDeleteModal({ show: false, id: '', name: '' });
    }
  };

  const getTriggerLabel = (trigger: Automation['trigger']) => {
    switch (trigger.type) {
      case 'manual':
        return 'Executar manualmente';
      case 'emailReceived':
        return `Ao receber email${trigger.labelFilter ? ` (${trigger.labelFilter})` : ''}`;
      case 'scheduled':
        return `Agendado para ${trigger.scheduleTime}`;
      default:
        return 'Desconhecido';
    }
  };

  const getTriggerIcon = (type: string) => {
    switch (type) {
      case 'manual':
        return Play;
      case 'emailReceived':
        return Mail;
      case 'scheduled':
        return Clock;
      default:
        return Zap;
    }
  };

  // Mostrar loading enquanto verifica conexão
  if (isLoadingTokenConfig) {
    return (
      <Container>
        <Content>
          <EmptyState>
            <Loader2 className="spinning" />
            <h2>Verificando conexão Gmail...</h2>
          </EmptyState>
        </Content>
      </Container>
    );
  }

  // Se não tiver token, não renderizar (useEffect irá redirecionar)
  if (!hasToken) {
    return null;
  }

  return (
    <>
      <Container>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <BackButton onClick={() => navigate('/gmail')}>
              <ArrowLeft size={20} />
              Voltar
            </BackButton>
            <HeaderLeft>
              <Title>Automações Gmail</Title>
              <Subtitle>
                {automations.length === 0
                  ? 'Nenhuma automação criada ainda'
                  : `${automations.length} automação${automations.length > 1 ? 'ões' : ''} ${automations.filter(a => a.active).length} ativa${automations.filter(a => a.active).length !== 1 ? 's' : ''}`
                }
              </Subtitle>
            </HeaderLeft>
          </div>
          <CreateButton onClick={() => navigate('/gmail/automations/new')}>
            <Plus size={20} />
            Nova Automação
          </CreateButton>
        </Header>

        <Content>
          {isLoading ? (
            <EmptyState>
              <Loader2 className="spinning" />
              <h2>Carregando automações...</h2>
            </EmptyState>
          ) : automations.length === 0 ? (
            <EmptyState>
              <Zap />
              <h2>Nenhuma automação ainda</h2>
              <p>
                Crie sua primeira automação para começar a economizar tempo e automatizar
                tarefas repetitivas do Gmail
              </p>
            </EmptyState>
          ) : (
            <AutomationsGrid>
              {automations.map((automation) => {
                const TriggerIcon = getTriggerIcon(automation.trigger.type);

                return (
                  <AutomationCard
                    key={automation.id}
                    active={automation.active}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CardHeader>
                      <CardTitle>{automation.name}</CardTitle>
                      <StatusBadge active={automation.active}>
                        {automation.active ? 'Ativa' : 'Rascunho'}
                      </StatusBadge>
                    </CardHeader>

                    <TriggerInfo>
                      <TriggerIcon size={18} />
                      <span>{getTriggerLabel(automation.trigger)}</span>
                    </TriggerInfo>

                    <CardMeta>
                      <span>
                        <Mail size={14} />
                        {automation.actions.length} ação{automation.actions.length > 1 ? 'ões' : ''}
                      </span>
                      <span>
                        {automation.createdAt.toLocaleDateString('pt-BR')}
                      </span>
                    </CardMeta>

                    <CardActions>
                      <ToggleSwitch>
                        <input
                          type="checkbox"
                          checked={automation.active}
                          onChange={() => handleToggleActive(automation.id)}
                        />
                        <span className="switch" />
                        <span className="label">
                          {automation.active ? 'Ativa' : 'Ativar'}
                        </span>
                      </ToggleSwitch>

                      <IconButton onClick={() => handleEdit(automation.id)}>
                        <Edit size={18} />
                      </IconButton>

                      <IconButton
                        className="delete"
                        onClick={() => handleDelete(automation.id)}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </CardActions>
                  </AutomationCard>
                );
              })}
            </AutomationsGrid>
          )}
        </Content>
      </Container>

      {/* Modal de confirmação de delete */}
      <AnimatePresence>
        {deleteModal.show && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDeleteModal({ show: false, id: '', name: '' })}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <AlertTriangle size={24} />
                <div>
                  <ModalTitle>Confirmar exclusão</ModalTitle>
                </div>
              </ModalHeader>

              <ModalText>
                Tem certeza que deseja deletar a automação <strong>"{deleteModal.name}"</strong>?
                Esta ação não pode ser desfeita.
              </ModalText>

              <ModalActions>
                <ModalButton
                  variant="secondary"
                  onClick={() => setDeleteModal({ show: false, id: '', name: '' })}
                >
                  Cancelar
                </ModalButton>
                <ModalButton
                  variant="danger"
                  onClick={confirmDelete}
                >
                  <Trash2 size={18} />
                  Deletar
                </ModalButton>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

    </>
  );
};

export default AutomationsList;
